<script setup lang="ts">
/**
 * The cards a student keeps with us.
 *
 * Lessons are charged two days ahead with nobody at the keyboard, so a card that
 * has expired stops lessons happening rather than merely failing a checkout.
 * This is where that gets fixed, which is why both payment emails link here.
 */
import { ref, onMounted, computed } from 'vue'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { Button } from '@/Components/ui/button'
import { CreditCard, Check, Trash2, Plus, AlertCircle } from 'lucide-vue-next'
import PaymentElementForm from '@/Components/Payment/PaymentElementForm.vue'
import { useCardSetup } from '@/composables/useCardSetup'
import { chargeLessonsTo, listCards, type SavedCard } from '@/lib/savedCards'

const props = defineProps<{
  stripeKey: string
}>()

const emit = defineEmits<{
  changed: []
}>()

const cards = ref<SavedCard[]>([])
const isLoading = ref(true)
const isSaving = ref(false)
const error = ref('')
const setup = useCardSetup()

/*
 * Removing the last card is allowed but worth a word: lessons are charged
 * without anybody present, so there would be nothing to charge them to.
 */
const isLastCard = computed(() => cards.value.length === 1)

onMounted(load)

async function load() {
  try {
    cards.value = await listCards()
  } catch {
    error.value = 'We could not load your cards. Please refresh the page.'
  } finally {
    isLoading.value = false
  }
}

async function startAdding() {
  error.value = ''

  try {
    await setup.open()
  } catch {
    error.value = 'We could not open the card form. Please try again.'
  }
}

async function addCard() {
  if (!setup.elements.value) return

  isSaving.value = true
  error.value = ''

  try {
    /*
     * Adding a card is almost always "the one you have is no good" — an expired
     * card, a replaced one, a lesson that failed to charge. Saving it without
     * making it the one we charge leaves the dead card in place and the lessons
     * still failing, with nothing on screen to say why.
     */
    cards.value = await chargeLessonsTo(await setup.save())
    setup.close()
    emit('changed')
  } catch (failure) {
    error.value = failure instanceof Error && failure.message ? failure.message : 'We could not save that card. Please try again.'
  } finally {
    isSaving.value = false
  }
}

async function makeDefault(card: SavedCard) {
  cards.value = await chargeLessonsTo(card.id)
  emit('changed')
}

async function remove(card: SavedCard) {
  const warning = isLastCard.value
    ? 'This is your only card. Without one we cannot take payment for your lessons, and each will need paying for by hand. Remove it anyway?'
    : `Remove the ${card.brand} ending ${card.last_four}?`

  if (!window.confirm(warning)) return

  const { data } = await window.axios.delete(`/student/settings/cards/${card.id}`)
  cards.value = data.cards
  emit('changed')
}
</script>

<template>
  <Card>
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <CreditCard class="h-5 w-5 text-gray-500" />
        Your cards
      </CardTitle>
      <CardDescription>
        Lessons are paid for 48 hours before they happen, so keep at least one card here that works.
      </CardDescription>
    </CardHeader>

    <CardContent class="space-y-4">
      <p v-if="error" class="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
        <AlertCircle class="mt-0.5 h-4 w-4 shrink-0" />
        {{ error }}
      </p>

      <p v-if="isLoading" class="text-sm text-gray-500">Loading your cards…</p>

      <p v-else-if="cards.length === 0 && !setup.setupSecret.value" class="text-sm text-gray-500">
        You have no card saved. Lessons cannot be paid for until you add one.
      </p>

      <ul v-else-if="cards.length" class="divide-y divide-gray-100">
        <li v-for="card in cards" :key="card.id" class="flex items-center justify-between gap-4 py-3">
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900">
              {{ card.brand }} ending {{ card.last_four }}
              <span
                v-if="card.is_default"
                class="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
              >
                <Check class="mr-0.5 inline h-3 w-3" />Used for lessons
              </span>
            </p>
            <p class="text-xs text-gray-500">Expires {{ card.expires }}</p>
          </div>

          <div class="flex shrink-0 items-center gap-2">
            <Button v-if="!card.is_default" variant="outline" size="sm" @click="makeDefault(card)">
              Use this one
            </Button>
            <Button variant="ghost" size="sm" class="text-red-600 hover:text-red-700" @click="remove(card)">
              <Trash2 class="h-4 w-4" />
              <span class="sr-only">Remove</span>
            </Button>
          </div>
        </li>
      </ul>

      <div v-if="setup.setupSecret.value" class="space-y-4 rounded-lg border border-gray-200 p-4">
        <PaymentElementForm
          :options="{ clientSecret: setup.setupSecret.value }"
          :stripe-key="props.stripeKey"
          @ready="setup.onReady"
        />
        <div class="flex gap-2">
          <Button :disabled="isSaving || !setup.elements.value" @click="addCard">
            {{ isSaving ? 'Saving…' : 'Save card' }}
          </Button>
          <Button variant="ghost" :disabled="isSaving" @click="setup.close">Cancel</Button>
        </div>
      </div>

      <Button v-else variant="outline" @click="startAdding">
        <Plus class="mr-1 h-4 w-4" />
        Add a card
      </Button>
    </CardContent>
  </Card>
</template>
